'use client';

import { useEffect, useState } from 'react';
import { Heart } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';
import { hasUserLikedPost, likePost, unlikePost, getPostLikeCount, type BlogPostDocument } from '../../firebase/blog-db';
import { trackEvent } from '../../lib/analytics';
import { Button } from '../ui/button';
import { cn } from '../ui/utils';

interface LikeButtonProps {
  post: BlogPostDocument;
  onRequireAuth: () => void;
  size?: 'sm' | 'default';
}

export function LikeButton({ post, onRequireAuth, size = 'default' }: LikeButtonProps) {
  const { user, isAuthenticated } = useAuth();
  const [liked, setLiked] = useState(false);
  const [count, setCount] = useState(post.likeCount ?? 0);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const liveCount = await getPostLikeCount(post.id!);
      if (cancelled) return;
      setCount(liveCount);
      if (isAuthenticated && user) {
        const has = await hasUserLikedPost(user.id, post.id!);
        if (!cancelled) setLiked(has);
      }
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [post.id, isAuthenticated, user]);

  const toggle = async () => {
    if (!isAuthenticated || !user) {
      onRequireAuth();
      return;
    }
    if (busy) return;
    setBusy(true);
    const wasLiked = liked;
    // Optimistic update
    setLiked(!wasLiked);
    setCount(c => c + (wasLiked ? -1 : 1));
    try {
      if (wasLiked) {
        await unlikePost(user.id, post.id!);
        trackEvent('article_unlike', { slug: post.slug });
      } else {
        await likePost(user.id, post);
        trackEvent('article_like', { slug: post.slug });
      }
    } catch {
      // revert on failure
      setLiked(wasLiked);
      setCount(c => c + (wasLiked ? 1 : -1));
      toast.error('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size={size === 'sm' ? 'sm' : 'default'}
      onClick={toggle}
      disabled={busy}
      aria-pressed={liked}
      aria-label={liked ? 'Unlike this article' : 'Like this article'}
      className={cn(liked && 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-600')}
    >
      <Heart className={cn('w-4 h-4', liked && 'fill-red-500 text-red-500')} />
      <span>{loaded ? count : post.likeCount}</span>
    </Button>
  );
}
